import type { DiscoveryTaskInfo } from "@/lib/api";

/**
 * One discovery chain: every discovery_tasks row of one (adapter, suburb, listing type).
 *
 * A continuation fork seeds its funnel totals from its parent (seedRawTotal etc. in the Firebase
 * DiscoveryTaskDoc), so within a chain the highest total is the chain's cumulative value and pages
 * SUM (each fork crawls disjoint pages). A buy and a sold task of one suburb are two independent
 * searches, not forks, so they are two chains (follow-until-sold phase 1, review finding 7).
 */
export interface DiscoveryChain {
  key: string;
  adapter_slug: string;
  suburb_name: string;
  state: string;
  listingType: string;
  totalPages: number;
  urlsFound: number;
  urlsRawTotal: number;
  urlsInSuburbTotal: number;
  urlsNewTotal: number;
  urlsOutOfScopeTotal: number;
  chainLength: number;
  statuses: string[];
  searchUrl: string | null;
  errorMessage: string | null;
  consecutiveEmptyPages: number;
}

export function aggregateDiscoveryTasks(tasks: DiscoveryTaskInfo[]): DiscoveryChain[] {
  const chains = new Map<string, DiscoveryChain>();
  for (const dt of tasks) {
    const key = `${dt.adapter_slug}::${dt.suburb_name}::${dt.listing_type}`;
    const raw = dt.urls_raw_total ?? 0;
    const inSuburb = dt.urls_in_suburb_total ?? 0;
    const enqueued = dt.urls_new_total ?? dt.urls_found ?? 0;
    const outOfScope = dt.urls_out_of_scope_total ?? 0;
    const chain = chains.get(key);
    if (!chain) {
      chains.set(key, {
        key,
        adapter_slug: dt.adapter_slug,
        suburb_name: dt.suburb_name,
        state: dt.state,
        listingType: dt.listing_type,
        totalPages: dt.current_page ?? 0,
        urlsFound: dt.urls_found ?? 0,
        urlsRawTotal: raw,
        urlsInSuburbTotal: inSuburb,
        urlsNewTotal: enqueued,
        urlsOutOfScopeTotal: outOfScope,
        chainLength: 1,
        statuses: [dt.status],
        searchUrl: dt.search_url ?? null,
        errorMessage: dt.error_message ?? null,
        consecutiveEmptyPages: dt.consecutive_empty_pages ?? 0,
      });
      continue;
    }
    chain.totalPages += dt.current_page ?? 0;
    chain.chainLength += 1;
    chain.statuses.push(dt.status);
    chain.urlsFound = Math.max(chain.urlsFound, dt.urls_found ?? 0);
    chain.urlsRawTotal = Math.max(chain.urlsRawTotal, raw);
    chain.urlsInSuburbTotal = Math.max(chain.urlsInSuburbTotal, inSuburb);
    chain.urlsNewTotal = Math.max(chain.urlsNewTotal, enqueued);
    chain.urlsOutOfScopeTotal = Math.max(chain.urlsOutOfScopeTotal, outOfScope);
    chain.searchUrl ??= dt.search_url ?? null;
    chain.errorMessage ??= dt.error_message ?? null;
    chain.consecutiveEmptyPages = Math.max(
      chain.consecutiveEmptyPages,
      dt.consecutive_empty_pages ?? 0,
    );
  }
  return Array.from(chains.values()).sort(
    (a, b) =>
      a.suburb_name.localeCompare(b.suburb_name) ||
      a.adapter_slug.localeCompare(b.adapter_slug) ||
      a.listingType.localeCompare(b.listingType),
  );
}

/** Operator wording for a discovery lane. */
export const LISTING_LANE_LABELS: Record<string, string> = {
  buy: "for sale",
  sold: "sold",
  rent: "for rent",
};
